import React, { useState, useEffect } from "react";
import { collection, addDoc, getDocs } from "firebase/firestore";
import { db } from "./firebase";

function BookLessonModal({ onClose }) {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [frequency, setFrequency] = useState("one-off");
  const [subject, setSubject] = useState("");
  const [duration, setDuration] = useState("60");
  const [notes, setNotes] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const fetchStudents = async () => {
      const querySnapshot = await getDocs(collection(db, "users"));
      const studentList = querySnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((user) => user.role === "student");
      setStudents(studentList);
    };
    fetchStudents();
  }, []);

  const handleBooking = async (e) => {
    e.preventDefault();
  
    if (!selectedStudent || !date || !time || !frequency || !subject) {
      setMessage("❌ Please complete all fields.");
      return;
    }
  
    try {
      const baseDate = new Date(`${date}T${time}`);
      const lessonsToAdd = [];
  
      const occurrences = frequency === "weekly" ? 10 : frequency === "fortnightly" ? 5 : 1;
      const intervalDays = frequency === "weekly" ? 7 : frequency === "fortnightly" ? 14 : 0;
  
      for (let i = 0; i < occurrences; i++) {
        const nextDate = new Date(baseDate);
        nextDate.setDate(baseDate.getDate() + i * intervalDays);
  
        const isoDate = nextDate.toISOString().split("T")[0];
        const isoTime = nextDate.toTimeString().slice(0, 5); // "HH:MM"
  
        lessonsToAdd.push({
          studentId: selectedStudent,
          date: isoDate,
          time: isoTime,
          frequency,
          subject,
          duration,
          notes,
          createdAt: new Date(),
        });
      }
  
      // Save all at once
      const bookingsRef = collection(db, "bookings");
      await Promise.all(lessonsToAdd.map((lesson) => addDoc(bookingsRef, lesson)));
  
      setMessage("✅ Lessons booked successfully!");
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error("Error booking lesson:", error);
      setMessage("❌ Failed to book lesson.");
    }
  };
  

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2>📚 Book a Lesson</h2>
        <form onSubmit={handleBooking} style={styles.form}>
          <select
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
            required
            style={styles.input}
          >
            <option value="">Select a student</option>
            {students.map((student) => (
              <option key={student.id} value={student.id}>
                {student.displayName} {student.surname}
              </option>
            ))}
          </select>

          <select
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            style={styles.input}
          >
            <option value="">Select subject</option>
            <option value="GCSE Maths">📐 GCSE Maths</option>
            <option value="A-Level Maths">📐 A-Level Maths</option>
            <option value="GCSE French">🇫🇷 GCSE French</option>
            <option value="A-Level French">🇫🇷 A-Level French</option>
          </select>

          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            style={styles.input}
          />

          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            required
            style={styles.input}
          />

          <select
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            required
            style={styles.input}
          >
            <option value="">Select duration</option>
            <option value="30">🕒 30 minutes</option>
            <option value="45">🕒 45 minutes</option>
            <option value="60">🕐 1 hour</option>
            <option value="90">🕑 1 hour 30 minutes</option>
            <option value="120">🕑 2 hours</option>
          </select>

          <select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
            required
            style={styles.input}
          >
            <option value="one-off">📆 One-off</option>
            <option value="weekly">🔁 Weekly</option>
            <option value="fortnightly">⏱️ Fortnightly</option>
          </select>

          <textarea
            placeholder="Optional notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ ...styles.input, height: "100px", resize: "vertical" }}
          />

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button type="submit" style={styles.button}>Book</button>
            <button type="button" onClick={onClose} style={styles.cancel}>Cancel</button>
          </div>
        </form>

        {message && <p style={styles.message}>{message}</p>}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "rgba(0,0,0,0.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modal: {
    background: "#fff",
    padding: "2rem",
    borderRadius: "12px",
    maxWidth: "500px",
    width: "100%",
    boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
    fontFamily: "'Poppins', sans-serif",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  input: {
    padding: "0.8rem",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "1rem",
  },
  button: {
    padding: "0.75rem 1.2rem",
    backgroundColor: "#4CAF50",
    color: "white",
    fontWeight: "bold",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  cancel: {
    padding: "0.75rem 1.2rem",
    backgroundColor: "#eee",
    color: "#333",
    fontWeight: "bold",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  message: {
    marginTop: "1rem",
    fontWeight: "bold",
  },
};

export default BookLessonModal;
